/**
 * Before / after portfolio data.
 *
 * TRUTHFULNESS:
 * - isVerifiedRealProject must be true only for confirmed Revolin work with approved evidence.
 * - imageSource "illustrative" | "generated" | "development"
 *   (internal enums; never market as completed Revolin projects).
 * - Each project supports 10 image slots (5 before + 5 after); only imageQaStatus "accepted"
 *   renders publicly.
 * - City fields describe the architectural setting shown in the gallery imagery.
 *
 * Placeholder reconciliation (2026-08): retired the original service-template placeholder
 * entries that reused the 13 single-pair illustrative-* assets plus Lovable homepage BA
 * photos. Those legacy files remain on disk under public/portfolio/ and src/assets/ for
 * reference; the gallery is now the 33 portfolio sets (pilot + batches A/B/C).
 */

export type ImageQaStatus = "accepted" | "pending" | "rejected";

export type ProjectImage = {
  src: string;
  alt: string;
  caption?: string | undefined;
  imageQaStatus?: ImageQaStatus | undefined;
  imageQaNotes?: string | undefined;
  generationAttemptCount?: number | undefined;
};

export type BeforeAfterProject = {
  id: string;
  slug: string;
  title: string;
  /** Legacy display location for homepage compatibility */
  location: string;
  /** Stable city ID matching service-areas.ts slugs */
  city: string | null;
  system: string;
  roofType: string | null;
  service: string | null;
  shortProblem: string | null;
  problem: string | null;
  diagnosis: string | null;
  solution: string | null;
  materials: readonly string[] | null;
  completionDate: string | null;
  projectStatus: "illustrative" | "verified" | "draft";
  isVerifiedRealProject: boolean;
  imageSource: "development" | "illustrative" | "generated" | "verified-photography";
  /** Legacy single-image fields for homepage slider */
  before: ProjectImage;
  after: ProjectImage;
  beforeImages: ProjectImage[];
  afterImages: ProjectImage[];
  featuredImage: ProjectImage;
  relatedServices: readonly string[];
  relatedLocation: string | null;
  width: number;
  height: number;
  isPlaceholder: boolean;
};

export function acceptedProjectImages(images: readonly ProjectImage[]): ProjectImage[] {
  return images.filter((image) => (image.imageQaStatus ?? "accepted") === "accepted");
}

function buildProject(
  input: Omit<BeforeAfterProject, "before" | "after" | "featuredImage" | "width" | "height"> & {
    width?: number;
    height?: number;
  },
): BeforeAfterProject {
  const acceptedBefore = acceptedProjectImages(input.beforeImages);
  const acceptedAfter = acceptedProjectImages(input.afterImages);
  const before = acceptedBefore[0] ?? input.beforeImages[0]!;
  const after = acceptedAfter[0] ?? input.afterImages[0]!;
  const featuredImage = acceptedAfter[0] ?? acceptedBefore[0] ?? after;
  return {
    ...input,
    before,
    after,
    featuredImage,
    width: input.width ?? 1280,
    height: input.height ?? 720,
  };
}

export const beforeAfterProjects: readonly BeforeAfterProject[] = [
  buildProject({
    id: "pilot-asphalt",
    slug: "asphalt-shingle-replacement-burnaby",
    title: "Roofing Transformation: Architectural Shingle Replacement",
    location: "Metro Vancouver setting typical of Burnaby and East Vancouver bungalows (example)",
    city: "burnaby",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "asphalt-shingle-replacement",
    shortProblem: "Typical of Burnaby and East Vancouver bungalows.",
    problem:
      "Aged asphalt on a craftsman bungalow setting typical of Burnaby and East Vancouver shows curling eave tabs, moss in north-facing valleys, granule loss, and worn ridge caps. House massing, cream lap siding, brick chimney, and white gutters stay constant.",
    diagnosis:
      "Wear concentrates at eaves, valleys, and ridge lines where Pacific Northwest moisture and shade accelerate aging. The scenario frames a full steep-slope reroof rather than isolated patching because the field is broadly past repair-only condition.",
    solution:
      "A complete dark charcoal architectural shingle replacement with straight courses, renewed ridge caps, cleared valleys, and intact flashing while siding, chimney, and landscape stay unchanged.",
    materials: [
      "Architectural asphalt shingles",
      "Ridge cap shingles",
      "Valley metal / woven valley detailing",
      "Ice-and-water underlayment (category)",
      "Step and chimney flashing (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-pilot-asphalt-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-pilot-asphalt-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-pilot-asphalt-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-pilot-asphalt-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-pilot-asphalt-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-pilot-asphalt-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-pilot-asphalt-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-pilot-asphalt-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-pilot-asphalt-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-pilot-asphalt-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Burnaby and East Vancouver bungalows",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Pilot QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["asphalt-shingle-replacement"],
    relatedLocation: "burnaby",
    isPlaceholder: true,
  }),
  buildProject({
    id: "asphalt-02",
    slug: "asphalt-shingle-replacement-surrey",
    title: "Roofing Transformation: Architectural Shingle Replacement",
    location: "Metro Vancouver setting typical of Surrey split-level homes (example)",
    city: "surrey",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "asphalt-shingle-replacement",
    shortProblem: "Typical of Surrey split-level homes.",
    problem:
      "A Surrey-style 1970s split-level with brown vinyl siding shows lifted three-tab shingles, worn valleys, and tired ridge caps across the steep-slope field - conditions typical of deferred asphalt replacement.",
    diagnosis:
      "Field-wide lift, valley granule loss, and ridge wear indicate the asphalt system is past reliable localized repair. The assessment points to a full architectural shingle reroof.",
    solution:
      "The after set depicts a complete charcoal architectural shingle roof with corrected coursing, renewed ridge caps, and cleaned valleys while split-level massing and landscape remain unchanged.",
    materials: [
      "Architectural asphalt shingles",
      "Ridge cap shingles",
      "Valley detailing",
      "Synthetic underlayment (category)",
      "Pipe boot and step flashing (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-asphalt-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Surrey split-level homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-asphalt-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Surrey split-level homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-asphalt-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Surrey split-level homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-asphalt-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Surrey split-level homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-asphalt-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Surrey split-level homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-asphalt-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Surrey split-level homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-asphalt-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Surrey split-level homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-asphalt-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Surrey split-level homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-asphalt-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Surrey split-level homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-asphalt-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Surrey split-level homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["asphalt-shingle-replacement"],
    relatedLocation: "surrey",
    isPlaceholder: true,
  }),
  buildProject({
    id: "emergency-01",
    slug: "emergency-roof-leak-repair-vancouver",
    title: "Roofing Transformation: Emergency Leak Stabilization",
    location: "Metro Vancouver setting typical of East Vancouver bungalows (example)",
    city: "vancouver",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "emergency-roof-leak-repair",
    shortProblem: "Typical of East Vancouver bungalows.",
    problem:
      "An East Vancouver-style stucco bungalow shows wind-lifted shingles around a plumbing vent, eave staining, and a localized opening that would admit water during rain.",
    diagnosis:
      "Damage concentrates at a vent penetration with runoff staining on fascia below. The scenario frames emergency stabilization and a matched local patch rather than a full reroof.",
    solution:
      "A localized shingle patch and new vent boot integrated into the aged field, with cleaned eave staining while most of the roof remains weathered.",
    materials: [
      "Matched asphalt shingles",
      "Replacement vent boot",
      "Underlayment patch (category)",
      "Sealant at boot flange (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-emergency-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of East Vancouver bungalows",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of East Vancouver bungalows",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of East Vancouver bungalows",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of East Vancouver bungalows",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of East Vancouver bungalows",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-emergency-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of East Vancouver bungalows",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of East Vancouver bungalows",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of East Vancouver bungalows",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of East Vancouver bungalows",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of East Vancouver bungalows",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["emergency-roof-leak-repair"],
    relatedLocation: "vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "emergency-02",
    slug: "emergency-roof-leak-repair-north-vancouver",
    title: "Roofing Transformation: Emergency Valley Repair",
    location: "Metro Vancouver setting typical of North Vancouver two-storey homes (example)",
    city: "north-vancouver",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "emergency-roof-leak-repair",
    shortProblem: "Typical of North Vancouver two-storey homes.",
    problem:
      "A North Vancouver-style two-storey with gray-blue siding shows valley debris dams, displaced shingles, and needle-clogged gutters after storm weather.",
    diagnosis:
      "Organic debris and a shingle gap at the valley create a concentrated water-entry risk while the broader aged field remains intact. Scope is valley clearing and woven local repair.",
    solution:
      "Clears the valley, reseats woven shingles, and cleans the gutter line while leaving the surrounding aged asphalt field unchanged.",
    materials: [
      "Matched asphalt shingles",
      "Valley metal or woven valley repair (category)",
      "Underlayment patch (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-emergency-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-emergency-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-emergency-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of North Vancouver two-storey homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["emergency-roof-leak-repair"],
    relatedLocation: "north-vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "spot-01",
    slug: "shingle-spot-repair-coquitlam",
    title: "Roofing Transformation: Localized Shingle Spot Repair",
    location: "Metro Vancouver setting typical of Coquitlam 1990s homes (example)",
    city: "coquitlam",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "shingle-spot-repair",
    shortProblem: "Typical of Coquitlam 1990s homes.",
    problem:
      "A Coquitlam-style 1990s home shows a mid-slope wind-torn patch with missing shingles and exposed underlayment while the rest of the asphalt field remains serviceable.",
    diagnosis:
      "Damage is localized; surrounding courses can accept a matched spot repair. The illustrated opening alone does not justify a full reroof.",
    solution:
      "Matched shingles into the torn area so underlayment is covered and edges integrate into the existing field, with only minor color variation at the patch.",
    materials: [
      "Matched asphalt shingles",
      "Underlayment patch (category)",
      "Roofing nails / sealant (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-spot-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-spot-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "After view 03",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "After view 04",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Coquitlam 1990s homes",
        caption: "After view 05",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["shingle-spot-repair"],
    relatedLocation: "coquitlam",
    isPlaceholder: true,
  }),
  buildProject({
    id: "spot-02",
    slug: "shingle-spot-repair-new-westminster",
    title: "Roofing Transformation: Vent-Area Spot Repair",
    location: "Metro Vancouver setting typical of New Westminster heritage-style homes (example)",
    city: "new-westminster",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "shingle-spot-repair",
    shortProblem: "Typical of New Westminster heritage-style homes.",
    problem:
      "A New Westminster heritage-style home shows cracked and curled shingles at a roof vent, creating a localized leak risk at the penetration.",
    diagnosis:
      "Failure is limited to the vent interface. Matched shingle replacement around a sound boot restores weather resistance without a full reroof.",
    solution:
      "Damaged shingles at the vent, reseats the interface, and leaves the remaining aged field and cream/green trim context unchanged.",
    materials: [
      "Matched asphalt shingles",
      "Vent boot inspection/replacement (category)",
      "Sealant at flange (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-spot-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-spot-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-spot-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of New Westminster heritage-style homes",
        caption: "After view 05",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: uniform re-roof scope + chimney continuity break after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["shingle-spot-repair"],
    relatedLocation: "new-westminster",
    isPlaceholder: true,
  }),
  buildProject({
    id: "flat-repair-01",
    slug: "flat-low-slope-roof-repair-richmond",
    title: "Roofing Transformation: Low-Slope Membrane Repair",
    location: "Metro Vancouver setting typical of Richmond rear additions (example)",
    city: "richmond",
    system: "Low-slope membrane",
    roofType: "Low-slope membrane",
    service: "flat-low-slope-roof-repair",
    shortProblem: "Typical of Richmond rear additions.",
    problem:
      "A Richmond-style rear addition with low-slope membrane shows ponding at a drain, blistered torch-on seams, and cracked transitions at the wall edge.",
    diagnosis:
      "Ponding and failed seams at drainage and wall transitions are classic low-slope leak points. Scope is membrane patching at the failure zones, not a full building reroof.",
    solution:
      "Patched SBS membrane, a dry drain area, heat-welded overlaps, and an intact wall transition with the main house unchanged.",
    materials: [
      "SBS modified bitumen patch materials",
      "Heat-welded seam detailing",
      "Drain flashing (category)",
      "Parapet transition membrane (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-flat-repair-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Richmond rear additions",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Richmond rear additions",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Richmond rear additions",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Richmond rear additions",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Richmond rear additions",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-flat-repair-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Richmond rear additions",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Richmond rear additions",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Richmond rear additions",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Richmond rear additions",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Richmond rear additions",
        caption: "After view 05",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["flat-low-slope-roof-repair"],
    relatedLocation: "richmond",
    isPlaceholder: true,
  }),
  buildProject({
    id: "flat-repair-02",
    slug: "flat-low-slope-roof-repair-burnaby",
    title: "Roofing Transformation: Strata Membrane Repair",
    location: "Metro Vancouver setting typical of Burnaby commercial/strata roofs (example)",
    city: "burnaby",
    system: "Low-slope membrane",
    roofType: "Low-slope membrane",
    service: "flat-low-slope-roof-repair",
    shortProblem: "Typical of Burnaby commercial/strata roofs.",
    problem:
      "A Burnaby commercial/strata-style roof shows split membrane at a parapet corner and an open pitch-pocket at a penetration near HVAC units.",
    diagnosis:
      "Parapet flashing failure and failed penetration seals are localized waterproofing defects. Rooftop units remain in place; scope is membrane and pitch-pocket repair.",
    solution:
      "Reseals the parapet corner, rebuilds the pitch-pocket, and restores coping detail while rooftop equipment stays fixed.",
    materials: [
      "Torch-on / SBS membrane repair materials",
      "Pitch-pocket filler and flashing",
      "Parapet flashing (category)",
      "Coping sealant (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-flat-repair-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-flat-repair-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "After view 04",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-repair-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Burnaby commercial/strata roofs",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["flat-low-slope-roof-repair"],
    relatedLocation: "burnaby",
    isPlaceholder: true,
  }),
  buildProject({
    id: "moss-01",
    slug: "moss-removal-roof-gutter-coquitlam",
    title: "Roofing Transformation: Moss Removal & Gutter Clearing",
    location: "Metro Vancouver setting typical of West Coquitlam ranchers (example)",
    city: "coquitlam",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "moss-removal-roof-gutter",
    shortProblem: "Typical of West Coquitlam ranchers.",
    problem:
      "A West Coquitlam rancher-style home under maple and cedar canopy shows heavy north-slope moss mats and debris-clogged gutters that trap moisture against asphalt shingles.",
    diagnosis:
      "Soft organic growth and gutter blockage are maintenance issues. Shingles may remain aged after cleaning; the scenario is moss removal and drainage clearing, not a new roof.",
    solution:
      "Moss and gutter debris removed while weathered granules and aged shingle color remain - no full replacement appearance.",
    materials: [
      "Soft-wash / manual moss removal (method category)",
      "Gutter and downspout clearing",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-moss-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-moss-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "After view 01",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "After view 02",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of West Coquitlam ranchers",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["moss-removal-roof-gutter"],
    relatedLocation: "coquitlam",
    isPlaceholder: true,
  }),
  buildProject({
    id: "moss-02",
    slug: "moss-removal-roof-gutter-maple-ridge",
    title: "Roofing Transformation: Moss Removal & Gutter Clearing",
    location: "Metro Vancouver setting typical of Maple Ridge forest-edge homes (example)",
    city: "maple-ridge",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "moss-removal-roof-gutter",
    shortProblem: "Typical of Maple Ridge forest-edge homes.",
    problem:
      "A Maple Ridge forest-edge two-storey shows moss along shaded eaves and ridge lines plus leaf-packed gutters from overhanging conifers.",
    diagnosis:
      "Shade-driven moss and organic gutter blockage need cleaning maintenance. The roof field should read as aged cleaned asphalt afterward, not newly installed product.",
    solution:
      "Moss from eaves and ridge and restores flowing gutters while granule-worn shingles remain in place.",
    materials: ["Moss removal (method category)", "Gutter and downspout flush"],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-moss-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-moss-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "After view 01",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "After view 03",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "After view 04",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Maple Ridge forest-edge homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["moss-removal-roof-gutter"],
    relatedLocation: "maple-ridge",
    isPlaceholder: true,
  }),
  buildProject({
    id: "gutter-replacement-01",
    slug: "gutter-replacement-delta",
    title: "Roofing Transformation: Seamless Gutter Replacement",
    location: "Metro Vancouver setting typical of Delta 1980s homes (example)",
    city: "delta",
    system: "Eave drainage",
    roofType: "Eave drainage",
    service: "gutter-replacement",
    shortProblem: "Typical of Delta 1980s homes.",
    problem:
      "A Delta 1980s-style home shows sagging, rusted, separated gutters and peeling fascia paint while the asphalt roof field is otherwise unchanged.",
    diagnosis:
      "Failed gutter joints and detachment from fascia are eave-drainage failures. Scope is seamless gutter and downspout replacement with fascia prep - not a reroof.",
    solution:
      "Installs new seamless aluminum gutters and downspouts on a straight slope line with cleaned fascia while shingles stay as-found.",
    materials: ["Seamless aluminum K-style gutters", "Downspouts and brackets", "Fascia hangers"],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-gutter-replacement-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Delta 1980s homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Delta 1980s homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Delta 1980s homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Delta 1980s homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Delta 1980s homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-gutter-replacement-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Delta 1980s homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Delta 1980s homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Delta 1980s homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Delta 1980s homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Delta 1980s homes",
        caption: "After view 05",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["gutter-replacement"],
    relatedLocation: "delta",
    isPlaceholder: true,
  }),
  buildProject({
    id: "gutter-replacement-02",
    slug: "gutter-replacement-white-rock",
    title: "Roofing Transformation: Coastal Gutter Replacement",
    location: "Metro Vancouver setting typical of White Rock coastal homes (example)",
    city: "white-rock",
    system: "Eave drainage",
    roofType: "Eave drainage",
    service: "gutter-replacement",
    shortProblem: "Typical of White Rock coastal homes.",
    problem:
      "A White Rock coastal-style home shows corroded undersized gutters with joint leaks and siding stains below failed seams.",
    diagnosis:
      "Coastal corrosion and undersized troughs cause overflow staining. Replacement with properly sized continuous gutters addresses drainage without altering the roof covering.",
    solution:
      "Continuous new gutters, cleaned stain lines, and unchanged roof planes in a coastal setting.",
    materials: [
      "Seamless aluminum gutters (coastal-grade category)",
      "Downspouts",
      "Hangers and termination sealant",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-gutter-replacement-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of White Rock coastal homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of White Rock coastal homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of White Rock coastal homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of White Rock coastal homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of White Rock coastal homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-gutter-replacement-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of White Rock coastal homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of White Rock coastal homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of White Rock coastal homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Final orchestrator accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of White Rock coastal homes",
        caption: "After view 04",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-replacement-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of White Rock coastal homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["gutter-replacement"],
    relatedLocation: "white-rock",
    isPlaceholder: true,
  }),
  buildProject({
    id: "cedar-repair-01",
    slug: "cedar-shake-repair-north-vancouver",
    title: "Roofing Transformation: Cedar Shake Spot Repair",
    location: "Metro Vancouver setting typical of North Vancouver post-and-beam homes (example)",
    city: "north-vancouver",
    system: "Cedar shake",
    roofType: "Cedar shake",
    service: "cedar-shake-repair-restoration",
    shortProblem: "Typical of North Vancouver post-and-beam homes.",
    problem:
      "A North Vancouver post-and-beam setting under conifers shows silver-grey weathered cedar shakes with a localized missing/cracked patch and moss in shaded courses.",
    diagnosis:
      "Damage is patch-scale on an otherwise weathered shake field. Restoration replaces failed shakes and cleans moss without converting the roof to a full warm-tan replacement.",
    solution:
      "Warm-tan new shakes into the weathered field, cleans moss at the repair zone, and keeps ridge and surrounding silver-grey shakes dominant.",
    materials: [
      "Cedar shake replacements",
      "Cedar ridge/repair fasteners (category)",
      "Breathable underlayment patch (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-cedar-repair-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-cedar-repair-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of North Vancouver post-and-beam homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["cedar-shake-repair-restoration"],
    relatedLocation: "north-vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "cedar-repair-02",
    slug: "cedar-shake-repair-west-vancouver",
    title: "Roofing Transformation: Cedar Shake Spot Repair",
    location: "Metro Vancouver setting typical of West Vancouver mid-century homes (example)",
    city: "west-vancouver",
    system: "Cedar shake",
    roofType: "Cedar shake",
    service: "cedar-shake-repair-restoration",
    shortProblem: "Typical of West Vancouver mid-century homes.",
    problem:
      "A West Vancouver mid-century home with olive siding shows curled or slipped cedar shakes near the ridge and deteriorated ridge caps on an otherwise weathered field.",
    diagnosis:
      "Failure concentrates at ridge and slipped courses. Localized shake and ridge-cap repair preserves the weathered field rather than installing a full new cedar roof.",
    solution:
      "Renews ridge caps and slipped shakes as a visible patch while silver-grey weathered shakes remain across most planes.",
    materials: [
      "Cedar shake replacements",
      "Cedar ridge caps",
      "Fasteners / underlayment patch (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-cedar-repair-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "Before view 03",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-cedar-repair-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "After view 03",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-repair-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of West Vancouver mid-century homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["cedar-shake-repair-restoration"],
    relatedLocation: "west-vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "metal-repair-01",
    slug: "metal-roof-repair-langley",
    title: "Roofing Transformation: Metal Roof Localized Repair",
    location: "Metro Vancouver setting typical of Langley acreage farmhouses (example)",
    city: "langley",
    system: "Ribbed metal",
    roofType: "Ribbed metal",
    service: "metal-roof-repair-maintenance",
    shortProblem: "Typical of Langley acreage farmhouses.",
    problem:
      "A Langley acreage-style farmhouse with ribbed metal roofing shows localized corrosion, loose laps, and a failed penetration boot while panels otherwise remain in service.",
    diagnosis:
      "Issues are fastener/lap and penetration related on an existing ribbed system - not a standing-seam replacement scope.",
    solution:
      "Laps, renews the penetration boot, and treats localized corrosion while aged ribbed panels stay in place.",
    materials: [
      "Ribbed metal panel/lap sealant (category)",
      "Replacement pipe boot",
      "Fastener replacements",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-metal-repair-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-metal-repair-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 3,
      },
      {
        src: "/portfolio/illustrative-metal-repair-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "After view 04",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Langley acreage farmhouses",
        caption: "After view 05",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["metal-roof-repair-maintenance"],
    relatedLocation: "langley",
    isPlaceholder: true,
  }),
  buildProject({
    id: "metal-repair-02",
    slug: "metal-roof-repair-port-moody",
    title: "Roofing Transformation: Standing-Seam Localized Repair",
    location: "Metro Vancouver setting typical of Port Moody contemporary homes (example)",
    city: "port-moody",
    system: "Standing-seam metal",
    roofType: "Standing-seam metal",
    service: "metal-roof-repair-maintenance",
    shortProblem: "Typical of Port Moody contemporary homes.",
    problem:
      "A Port Moody contemporary home with dark standing-seam metal shows localized panel/ridge damage and weathered streaking elsewhere on the roof.",
    diagnosis:
      "Damage is limited to specific seams or ridge caps. Repair replaces or reseams the failed zone while the weathered standing-seam field remains.",
    solution:
      "The damaged hip/ridge or panel zone with a quiet localized fix; street-distance views stay subdued while close-ups show the repair contrast.",
    materials: [
      "Standing-seam panel or ridge cap replacements",
      "Sealant / clip fasteners (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-metal-repair-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "Before view 01",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 2,
      },
      {
        src: "/portfolio/illustrative-metal-repair-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "Before view 04",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-repair-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-metal-repair-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Accepted on orchestrator manual review",
        generationAttemptCount: 3,
      },
      {
        src: "/portfolio/illustrative-metal-repair-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 2,
      },
      {
        src: "/portfolio/illustrative-metal-repair-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Accepted on orchestrator manual review",
        generationAttemptCount: 3,
      },
      {
        src: "/portfolio/illustrative-metal-repair-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Accepted on orchestrator manual review",
        generationAttemptCount: 3,
      },
      {
        src: "/portfolio/illustrative-metal-repair-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Port Moody contemporary homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 2,
      },
    ],
    relatedServices: ["metal-roof-repair-maintenance"],
    relatedLocation: "port-moody",
    isPlaceholder: true,
  }),
  buildProject({
    id: "cedar-replacement-01",
    slug: "cedar-shake-replacement-west-vancouver",
    title: "Roofing Transformation: Cedar Shake Roof Replacement",
    location: "Metro Vancouver setting typical of West Vancouver craftsman homes (example)",
    city: "west-vancouver",
    system: "Cedar shake",
    roofType: "Cedar shake",
    service: "cedar-shake-roof-replacement",
    shortProblem: "Typical of West Vancouver craftsman homes.",
    problem:
      "A West Vancouver craftsman-style home shows an aged cedar shake roof at end of service life, with widespread weathering that exceeds spot-repair practicality.",
    diagnosis:
      "Field-wide shake deterioration supports full cedar replacement while stone/sage exterior identity stays locked for continuity.",
    solution:
      "A complete new cedar shake roof with straight coursing and renewed ridge details on the same craftsman massing.",
    materials: [
      "New cedar shakes",
      "Cedar ridge caps",
      "Breathable underlayment (category)",
      "Flashing renewal (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-cedar-replacement-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-cedar-replacement-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of West Vancouver craftsman homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["cedar-shake-roof-replacement"],
    relatedLocation: "west-vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "cedar-replacement-02",
    slug: "cedar-shake-replacement-north-vancouver",
    title: "Roofing Transformation: Cedar Shake Roof Replacement",
    location: "Metro Vancouver setting typical of North Vancouver Deep Cove cottages (example)",
    city: "north-vancouver",
    system: "Cedar shake",
    roofType: "Cedar shake",
    service: "cedar-shake-roof-replacement",
    shortProblem: "Typical of North Vancouver Deep Cove cottages.",
    problem:
      "A North Vancouver Deep Cove cottage among tall trees shows heavily weathered, mossy cedar shakes across primary roof planes.",
    diagnosis:
      "Shade and age have taken the shake system past maintenance-only condition. Full cedar replacement is the framed scope.",
    solution:
      "A complete new cedar shake roof with clean coursing under the same conifer canopy, without altering cottage massing.",
    materials: [
      "New cedar shakes",
      "Cedar ridge caps",
      "Underlayment (category)",
      "Step flashing (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-cedar-replacement-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-cedar-replacement-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-cedar-replacement-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of North Vancouver Deep Cove cottages",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["cedar-shake-roof-replacement"],
    relatedLocation: "north-vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "metal-replacement-01",
    slug: "metal-roof-replacement-west-vancouver",
    title: "Roofing Transformation: Metal Roof Replacement",
    location: "Metro Vancouver setting typical of West Vancouver modern homes (example)",
    city: "west-vancouver",
    system: "Standing-seam metal",
    roofType: "Standing-seam metal",
    service: "metal-roof-replacement",
    shortProblem: "Typical of West Vancouver modern homes.",
    problem:
      "A West Vancouver modern home shows an aged steep-slope covering ready for conversion to standing-seam metal, with black gutter language and white stucco accents held constant.",
    diagnosis:
      "The scope is a full metal roof replacement for longevity and clean modern lines, not a patch on failing asphalt or prior metal.",
    solution:
      "After views depict a complete standing-seam metal roof with consistent black gutters/fascia and unchanged modern facade identity.",
    materials: [
      "Standing-seam metal panels",
      "Metal ridge and eave trim",
      "Underlayment (category)",
      "Matching flashings",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-metal-replacement-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of West Vancouver modern homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of West Vancouver modern homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of West Vancouver modern homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of West Vancouver modern homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of West Vancouver modern homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-metal-replacement-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of West Vancouver modern homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of West Vancouver modern homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of West Vancouver modern homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of West Vancouver modern homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of West Vancouver modern homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["metal-roof-replacement"],
    relatedLocation: "west-vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "metal-replacement-02",
    slug: "metal-roof-replacement-coquitlam",
    title: "Roofing Transformation: Metal Roof Replacement",
    location: "Metro Vancouver setting typical of Coquitlam family homes (example)",
    city: "coquitlam",
    system: "Standing-seam metal",
    roofType: "Standing-seam metal",
    service: "metal-roof-replacement",
    shortProblem: "Typical of Coquitlam family homes.",
    problem:
      "A Coquitlam family home with beige siding shows an aged asphalt field framed for full standing-seam metal replacement across all visible planes.",
    diagnosis:
      "End-of-life asphalt and desire for a long-life metal system drive full replacement; continuity requires the same beige house identity on every shot.",
    solution:
      "Standing-seam metal on all visible roof planes with matching trim, not leftover shingle roofs adjacent.",
    materials: [
      "Standing-seam metal panels",
      "Ridge/hip metal caps",
      "Underlayment (category)",
      "Pipe boots and flashings",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-metal-replacement-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Coquitlam family homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Coquitlam family homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Coquitlam family homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Coquitlam family homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Coquitlam family homes",
        caption: "Before view 05",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-metal-replacement-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Coquitlam family homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Coquitlam family homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Coquitlam family homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Coquitlam family homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-metal-replacement-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Coquitlam family homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["metal-roof-replacement"],
    relatedLocation: "coquitlam",
    isPlaceholder: true,
  }),
  buildProject({
    id: "flat-replacement-01",
    slug: "flat-roof-replacement-vancouver",
    title: "Roofing Transformation: Flat Roof Membrane Replacement",
    location: "Metro Vancouver setting typical of Vancouver Mount Pleasant duplexes (example)",
    city: "vancouver",
    system: "Low-slope SBS membrane",
    roofType: "Low-slope SBS membrane",
    service: "flat-roof-replacement-sbs-tpo-pvc",
    shortProblem: "Typical of Vancouver Mount Pleasant duplexes.",
    problem:
      "A Vancouver Mount Pleasant-style flat-roof duplex shows an aged low-slope membrane with worn seams and penetrations typical of a full SBS replacement cycle.",
    diagnosis:
      "Membrane age and seam wear support full low-slope replacement rather than scattered patches. Penetration count/placement stay locked for continuity.",
    solution:
      "A new SBS membrane field with renewed penetrations and edges while duplex massing remains unchanged.",
    materials: [
      "SBS modified bitumen membrane",
      "Penetration flashings",
      "Base/cap sheet system (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-flat-replacement-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-flat-replacement-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Vancouver Mount Pleasant duplexes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["flat-roof-replacement-sbs-tpo-pvc"],
    relatedLocation: "vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "flat-replacement-02",
    slug: "flat-roof-replacement-richmond",
    title: "Roofing Transformation: Commercial Flat Roof Replacement",
    location: "Metro Vancouver setting typical of Richmond small commercial buildings (example)",
    city: "richmond",
    system: "Low-slope TPO membrane",
    roofType: "Low-slope TPO membrane",
    service: "flat-roof-replacement-sbs-tpo-pvc",
    shortProblem: "Typical of Richmond small commercial buildings.",
    problem:
      "A Richmond small commercial building shows an aged low-slope roof with HVAC units fixed in place, framed for full TPO membrane replacement.",
    diagnosis:
      "Commercial low-slope covering is at replacement age. Scope renews the membrane field while rooftop unit positions stay constant.",
    solution:
      "A clean new TPO field with sealed penetrations around the same HVAC layout in an industrial-park setting.",
    materials: [
      "TPO membrane",
      "Penetration boots and pitch details",
      "Parapet/edge metal (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-flat-replacement-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-flat-replacement-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flat-replacement-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Richmond small commercial buildings",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["flat-roof-replacement-sbs-tpo-pvc"],
    relatedLocation: "richmond",
    isPlaceholder: true,
  }),
  buildProject({
    id: "flashing-01",
    slug: "flashing-repair-burnaby",
    title: "Roofing Transformation: Step Flashing Renewal",
    location: "Metro Vancouver setting typical of Burnaby 1960s split-levels (example)",
    city: "burnaby",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "flashing-roof-penetration-repair",
    shortProblem: "Typical of Burnaby 1960s split-levels.",
    problem:
      "A Burnaby 1960s split-level shows rusted, lifted step flashing and messy sealant at a roof-to-wall transition on aged dark shingles.",
    diagnosis:
      "Sidewall flashing has failed while the surrounding asphalt field remains aged but serviceable. Scope is woven step-flashing renewal, not a reroof.",
    solution:
      "Woven step flashing lapped with shingle courses and siding, removes sealant smears, and keeps the aged field.",
    materials: [
      "Galvanized or prefinished step flashing",
      "Wall counter-flashing / siding integration",
      "Underlayment at transition (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-flashing-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-flashing-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "After view 01",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "After view 03",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "After view 04",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Burnaby 1960s split-levels",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["flashing-roof-penetration-repair"],
    relatedLocation: "burnaby",
    isPlaceholder: true,
  }),
  buildProject({
    id: "flashing-02",
    slug: "flashing-repair-vancouver",
    title: "Roofing Transformation: Vent Boot Flashing Repair",
    location: "Metro Vancouver setting typical of Vancouver east-side homes (example)",
    city: "vancouver",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "flashing-roof-penetration-repair",
    shortProblem: "Typical of Vancouver east-side homes.",
    problem:
      "A Vancouver east-side home shows a UV-cracked rubber vent boot with staining on courses below the black ABS pipe.",
    diagnosis:
      "The penetration boot flange has failed. Localized boot replacement into the aged shingle field restores the seal without re-roofing.",
    solution:
      "A new boot under/over courses correctly and clears stain context while surrounding shingles stay visibly aged.",
    materials: ["Replacement vent boot", "Matched shingle cuts", "Sealant at flange (category)"],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-flashing-02-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Vancouver east-side homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-02-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Vancouver east-side homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-02-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Vancouver east-side homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-02-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Vancouver east-side homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-02-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Vancouver east-side homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-flashing-02-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Vancouver east-side homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-02-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Vancouver east-side homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-02-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Vancouver east-side homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-02-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Vancouver east-side homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-flashing-02-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Vancouver east-side homes",
        caption: "After view 05",
        imageQaStatus: "rejected",
        imageQaNotes:
          "final reject: repair-scope violation after exhausted regeneration budget — file relocated to docs/internal/rejected-portfolio/ (not published)",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["flashing-roof-penetration-repair"],
    relatedLocation: "vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "skylight-01",
    slug: "skylight-flashing-repair-north-vancouver",
    title: "Roofing Transformation: Skylight Flashing Repair",
    location: "Metro Vancouver setting typical of North Vancouver 1990s homes (example)",
    city: "north-vancouver",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "skylight-leak-flashing-repair",
    shortProblem: "Typical of North Vancouver 1990s homes.",
    problem:
      "A North Vancouver 1990s home with a curb-mounted skylight shows lifted curb shingles, gapped head flashing, and stain streaks below the sill.",
    diagnosis:
      "Skylight interface flashing - not the glass unit alone - is the leak path. Scope renews head, step, and apron flashing into the aged field.",
    solution:
      "A clean skylight flashing kit at head, sides, and sill while the surrounding architectural shingles remain aged.",
    materials: [
      "Skylight flashing kit (head/step/apron)",
      "Matched shingles at curb",
      "Ice-and-water at curb (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-skylight-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-skylight-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-skylight-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-skylight-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-skylight-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-skylight-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-skylight-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-skylight-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-skylight-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-skylight-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of North Vancouver 1990s homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["skylight-leak-flashing-repair"],
    relatedLocation: "north-vancouver",
    isPlaceholder: true,
  }),
  buildProject({
    id: "chimney-01",
    slug: "chimney-flashing-repair-new-westminster",
    title: "Roofing Transformation: Chimney Flashing Renewal",
    location: "Metro Vancouver setting typical of New Westminster heritage bungalows (example)",
    city: "new-westminster",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "chimney-flashing-repair",
    shortProblem: "Typical of New Westminster heritage bungalows.",
    problem:
      "A New Westminster heritage bungalow shows pulled chimney flashing, tar patches, counter-flashing gaps at brick, and cracked mortar at corners.",
    diagnosis:
      "Masonry chimney step and counter flashing have failed. Proper repair reglets counter-flashing into mortar and weaves step flashing - not surface-mounted strips alone.",
    solution:
      "Apron/step/counter flashing into aged brick and shingles, removing mastic-only “repairs.”",
    materials: [
      "Step flashing",
      "Counter-flashing regleted into mortar",
      "Apron flashing",
      "Mortar repair (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-chimney-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-chimney-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-chimney-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-chimney-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-chimney-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-chimney-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-chimney-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-chimney-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-chimney-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-chimney-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of New Westminster heritage bungalows",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["chimney-flashing-repair"],
    relatedLocation: "new-westminster",
    isPlaceholder: true,
  }),
  buildProject({
    id: "gutter-cleaning-01",
    slug: "gutter-cleaning-port-coquitlam",
    title: "Roofing Transformation: Gutter Cleaning & Downspout Flush",
    location: "Metro Vancouver setting typical of Port Coquitlam homes (example)",
    city: "port-coquitlam",
    system: "Eave drainage",
    roofType: "Eave drainage",
    service: "gutter-cleaning-downspout-flush",
    shortProblem: "Typical of Port Coquitlam homes.",
    problem:
      "A Port Coquitlam-style home shows leaf-packed gutters overflowing, with wet debris in the channel and overflow streaks on walls.",
    diagnosis:
      "Blockage - not gutter replacement - is the issue. Cleaning and downspout flushing restore drainage; guards are out of scope for this scenario.",
    solution:
      "Empty open K-style gutters and clear flowing downspouts on the same house, with aged roof retained and no guards installed.",
    materials: ["Gutter cleaning", "Downspout flush", "Debris disposal"],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Port Coquitlam homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Port Coquitlam homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Port Coquitlam homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Port Coquitlam homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Port Coquitlam homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Port Coquitlam homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Port Coquitlam homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Port Coquitlam homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Port Coquitlam homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-cleaning-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Port Coquitlam homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["gutter-cleaning-downspout-flush"],
    relatedLocation: "port-coquitlam",
    isPlaceholder: true,
  }),
  buildProject({
    id: "gutter-guard-01",
    slug: "gutter-guard-port-moody",
    title: "Roofing Transformation: Gutter Guard Installation",
    location: "Metro Vancouver setting typical of Port Moody conifer-edge homes (example)",
    city: "port-moody",
    system: "Eave drainage",
    roofType: "Eave drainage",
    service: "gutter-guard-installation",
    shortProblem: "Typical of Port Moody conifer-edge homes.",
    problem:
      "A Port Moody conifer-edge home shows white gutters packed with pine needles and exterior staining from chronic overflow.",
    diagnosis:
      "Needle load overwhelms open gutters. After cleaning, micro-mesh gutter guards are installed so debris sheds on top of the mesh.",
    solution:
      "Black mesh guards on the same white K-style runs, with needles resting on the mesh rather than filling the trough.",
    materials: [
      "Micro-mesh gutter guards",
      "Existing K-style gutter integration",
      "Fasteners / clips (category)",
    ],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-gutter-guard-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-guard-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-guard-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-guard-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-guard-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-gutter-guard-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-guard-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-guard-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-guard-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-gutter-guard-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Port Moody conifer-edge homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["gutter-guard-installation"],
    relatedLocation: "port-moody",
    isPlaceholder: true,
  }),
  buildProject({
    id: "ventilation-01",
    slug: "roof-ventilation-upgrade-burnaby",
    title: "Roofing Transformation: Attic Ventilation Upgrade",
    location: "Metro Vancouver setting typical of Burnaby split-level homes (example)",
    city: "burnaby",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "roof-ventilation-upgrade",
    shortProblem: "Typical of Burnaby split-level homes.",
    problem:
      "A Burnaby split-level shows box vents and ridge caps only - no continuous ridge vent - plus stained or under-ventilated soffit intake.",
    diagnosis:
      "Exhaust and intake are unbalanced. Upgrade adds continuous ridge vent and vented soffit panels; box vents should not remain as the primary exhaust story afterward.",
    solution:
      "Low-profile ridge vent along the peak and clean perforated soffit intake while house identity stays constant.",
    materials: ["Continuous ridge vent", "Vented soffit panels", "Ridge cap reshingle (category)"],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-ventilation-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Burnaby split-level homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-ventilation-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Burnaby split-level homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-ventilation-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Burnaby split-level homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-ventilation-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Burnaby split-level homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-ventilation-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Burnaby split-level homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-ventilation-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Burnaby split-level homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-ventilation-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Burnaby split-level homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-ventilation-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Burnaby split-level homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-ventilation-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Burnaby split-level homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-ventilation-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Burnaby split-level homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["roof-ventilation-upgrade"],
    relatedLocation: "burnaby",
    isPlaceholder: true,
  }),
  buildProject({
    id: "fascia-soffit-01",
    slug: "fascia-soffit-repair-delta",
    title: "Roofing Transformation: Fascia & Soffit Renewal",
    location: "Metro Vancouver setting typical of Delta homes with carports (example)",
    city: "delta",
    system: "Eave components",
    roofType: "Eave components",
    service: "fascia-soffit-repair-replacement",
    shortProblem: "Typical of Delta homes with carports.",
    problem:
      "A Delta home with carport shows obvious fascia rot, missing gutter section, and sagging water-damaged soffit at the eave.",
    diagnosis:
      "Eave wood components have failed from chronic moisture. Scope renews fascia and soffit (and reattaches gutter) without replacing the aged roof covering.",
    solution:
      "New white fascia and vented soffit along the damaged run while aged roofing and dark siding remain.",
    materials: ["Painted fascia boards", "Vented soffit panels", "Gutter reattachment hardware"],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-fascia-soffit-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Delta homes with carports",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-fascia-soffit-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Delta homes with carports",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-fascia-soffit-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Delta homes with carports",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-fascia-soffit-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Delta homes with carports",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-fascia-soffit-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Delta homes with carports",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-fascia-soffit-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Delta homes with carports",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-fascia-soffit-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Delta homes with carports",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-fascia-soffit-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Delta homes with carports",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-fascia-soffit-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Delta homes with carports",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-fascia-soffit-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Delta homes with carports",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["fascia-soffit-repair-replacement"],
    relatedLocation: "delta",
    isPlaceholder: true,
  }),
  buildProject({
    id: "storm-tarping-01",
    slug: "storm-tarping-surrey",
    title: "Roofing Transformation: Storm Tarping Dry-In",
    location: "Metro Vancouver setting typical of Surrey storm-damaged homes (example)",
    city: "surrey",
    system: "Damaged steep-slope",
    roofType: "Damaged steep-slope",
    service: "storm-tarping-emergency-dry-in",
    shortProblem: "Typical of Surrey storm-damaged homes.",
    problem:
      "A Surrey-style home after storm damage shows torn shingles, exposed deck, branches, and debris - an active weather opening.",
    diagnosis:
      "Immediate need is professional temporary dry-in so interiors stay protected until permanent repairs. Scope is battened tarping, not a finished reroof.",
    solution:
      "A blue tarp secured with wooden battens, top edge tucked under courses, fully covering the exposed deck opening.",
    materials: ["Heavy-duty tarp", "Wood battens", "Cap nails / fasteners"],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-storm-tarping-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-storm-tarping-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-storm-tarping-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-storm-tarping-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-storm-tarping-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-storm-tarping-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-storm-tarping-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-storm-tarping-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-storm-tarping-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 2,
      },
      {
        src: "/portfolio/illustrative-storm-tarping-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Surrey storm-damaged homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["storm-tarping-emergency-dry-in"],
    relatedLocation: "surrey",
    isPlaceholder: true,
  }),
  buildProject({
    id: "health-check-01",
    slug: "roof-health-check-langley",
    title: "Roofing Transformation: Roof Health Check Maintenance",
    location: "Metro Vancouver setting typical of Langley cream-stucco homes (example)",
    city: "langley",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "roof-health-check-maintenance",
    shortProblem: "Typical of Langley cream-stucco homes.",
    problem:
      "A Langley cream-stucco home shows maintenance findings: eave moss, a cracked vent boot, a lifted shingle tab, and chimney/wall flashing needing reseal.",
    diagnosis:
      "Issues are discrete maintenance items. After work should remain subtle - aged field retained - not a pristine full renewal.",
    solution:
      "The vent boot, flattens the lifted tab, and reseals chimney/wall flashing while mossy/worn eaves and aged shingles largely remain.",
    materials: ["Replacement vent boot", "Sealant / flashing reseal", "Hand-sealing lifted tabs"],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-health-check-01-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-health-check-01-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-health-check-01-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-health-check-01-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-health-check-01-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-health-check-01-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-health-check-01-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-health-check-01-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Accepted on orchestrator manual review",
        generationAttemptCount: 3,
      },
      {
        src: "/portfolio/illustrative-health-check-01-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Regenerated OK after independent reject",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-health-check-01-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of Langley cream-stucco homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["roof-health-check-maintenance"],
    relatedLocation: "langley",
    isPlaceholder: true,
  }),
  buildProject({
    id: "moss-03",
    slug: "moss-removal-roof-gutter-west-vancouver",
    title: "Roofing Transformation: Moss Removal & Gutter Clearing",
    location: "Metro Vancouver setting typical of West Vancouver cottage homes (example)",
    city: "west-vancouver",
    system: "Architectural asphalt",
    roofType: "Architectural asphalt",
    service: "moss-removal-roof-gutter",
    shortProblem: "Typical of West Vancouver cottage homes.",
    problem:
      "A West Vancouver cottage-style home under conifers shows heavy front-slope moss lifting shingle edges and spilling into a clogged gutter.",
    diagnosis:
      "Dense moss growth is a cleaning/maintenance scope. After images must retain an aged gray field, not a new-product roof.",
    solution:
      "Moss from the slope and gutter while residual algae film and aged granules remain believable on the cleaned field.",
    materials: ["Moss removal (method category)", "Gutter clearing"],
    completionDate: null,
    projectStatus: "illustrative",
    isVerifiedRealProject: false,
    imageSource: "illustrative",
    beforeImages: [
      {
        src: "/portfolio/illustrative-moss-03-before-01.jpg",
        alt: "Establishing property view before work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "Before view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-03-before-02.jpg",
        alt: "Primary problem-area detail before work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "Before view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-03-before-03.jpg",
        alt: "Tight technical failure detail before work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "Before view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-03-before-04.jpg",
        alt: "Alternate angle of the failure before work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "Before view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-03-before-05.jpg",
        alt: "Eave, gutter, or secondary consequence detail before work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "Before view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    afterImages: [
      {
        src: "/portfolio/illustrative-moss-03-after-01.jpg",
        alt: "Establishing property view after work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "After view 01",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-03-after-02.jpg",
        alt: "Matching repair or completion detail after work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "After view 02",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-03-after-03.jpg",
        alt: "Matching technical detail after work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "After view 03",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-03-after-04.jpg",
        alt: "Matching alternate angle after work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "After view 04",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
      {
        src: "/portfolio/illustrative-moss-03-after-05.jpg",
        alt: "Matching eave or secondary detail after work on a roofing scenario typical of West Vancouver cottage homes",
        caption: "After view 05",
        imageQaStatus: "accepted",
        imageQaNotes: "Independent QA accepted",
        generationAttemptCount: 1,
      },
    ],
    relatedServices: ["moss-removal-roof-gutter"],
    relatedLocation: "west-vancouver",
    isPlaceholder: true,
  }),
];

export function getProjectBySlug(slug: string): BeforeAfterProject | undefined {
  return beforeAfterProjects.find((project) => project.slug === slug);
}

/** Projects with at least one accepted before and after image (gallery-safe). */
export function publishableProjects(
  projects: readonly BeforeAfterProject[] = beforeAfterProjects,
): BeforeAfterProject[] {
  return projects.filter((project) => {
    const before = acceptedProjectImages(project.beforeImages);
    const after = acceptedProjectImages(project.afterImages);
    return before.length > 0 && after.length > 0;
  });
}

/** Featured strip for the homepage (keep short). */
export const homepageBeforeAfterProjects: readonly BeforeAfterProject[] =
  publishableProjects().slice(0, 3);

export function projectsForService(serviceSlug: string): BeforeAfterProject[] {
  return publishableProjects().filter(
    (project) => project.service === serviceSlug || project.relatedServices.includes(serviceSlug),
  );
}

export function filterProjects(filters: {
  service?: string | null;
  roofType?: string | null;
  city?: string | null;
}): BeforeAfterProject[] {
  const service = filters.service?.trim() || null;
  const roofType = filters.roofType?.trim() || null;
  const city = filters.city?.trim() || null;

  return publishableProjects().filter((project) => {
    if (service && project.service !== service) return false;
    if (roofType && project.roofType !== roofType) return false;
    if (city && project.city !== city) return false;
    return true;
  });
}

export function portfolioServiceOptions(): string[] {
  return [
    ...new Set(
      publishableProjects()
        .map((project) => project.service)
        .filter((value): value is string => Boolean(value)),
    ),
  ];
}

export function portfolioRoofTypeOptions(): string[] {
  return [
    ...new Set(
      publishableProjects()
        .map((project) => project.roofType)
        .filter((value): value is string => Boolean(value)),
    ),
  ].sort((a, b) => a.localeCompare(b));
}

/** Alias used by portfolio filters */
export function portfolioRoofTypes(): string[] {
  return portfolioRoofTypeOptions();
}

export function portfolioLocations(): string[] {
  return [
    ...new Set(
      publishableProjects()
        .map((project) => project.city)
        .filter((value): value is string => Boolean(value)),
    ),
  ].sort((a, b) => a.localeCompare(b));
}

export function projectPrimaryBefore(project: BeforeAfterProject): ProjectImage {
  return acceptedProjectImages(project.beforeImages)[0] ?? project.before;
}

export function projectPrimaryAfter(project: BeforeAfterProject): ProjectImage {
  return acceptedProjectImages(project.afterImages)[0] ?? project.after;
}

export function portfolioImageSlotTotal(): number {
  return publishableProjects().reduce(
    (sum, project) =>
      sum +
      acceptedProjectImages(project.beforeImages).length +
      acceptedProjectImages(project.afterImages).length,
    0,
  );
}
